import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import apiClient from '../services/apiClient';
import authService from '../services/AuthService';
import { unlockPracticeAudio } from '../services/soundEffects';
import {
  PLAYER_IMAGE_OPTIONS,
  getParticipantImage,
  normalizeCharacterId,
} from '../services/TournamentAssets';
import { getLanguage, t } from '../i18n';
import './TournamentPage.css';

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(getLanguage() === 'ko' ? 'ko-KR' : 'en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Seoul',
  });
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString(getLanguage() === 'ko' ? 'ko-KR' : 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Seoul',
  });
}

function getHumanCount(tournament) {
  return (tournament?.participants || []).filter((participant) => participant.type === 'human').length;
}

function getStatusCopy(tournament) {
  if (!tournament) return t('learningNavigation.tournament.loadingStatus');
  if (tournament.status === 'signup') return t('learningNavigation.tournament.signupOpen');
  if (tournament.status === 'active') return t('learningNavigation.tournament.activeStatus', undefined, { phase: getPhaseLabel({ phase: tournament.currentPhase, phaseLabel: tournament.currentPhaseLabel }) || t('learningNavigation.tournament.competition'), day: Math.min(tournament.currentDay || 1, 7) });
  if (tournament.status === 'completed') return t('learningNavigation.tournament.resultsReady');
  if (tournament.status === 'cancelled') return t('learningNavigation.tournament.cancelledStatus');
  return t('learningNavigation.tournament.nextStatus');
}

const KNOCKOUT_PHASES = ['roundOf16', 'quarterFinal', 'semiFinal', 'final'];
const BRACKET_PHASES = KNOCKOUT_PHASES;
const BRACKET_PHASE_LABELS = {
  roundOf16: 'learningNavigation.tournament.roundOf16',
  quarterFinal: 'learningNavigation.tournament.quarterFinal',
  semiFinal: 'learningNavigation.tournament.semiFinal',
  final: 'learningNavigation.tournament.final',
};
const BRACKET_LAYOUT = {
  roundOf16: { count: 8, x: 2, y: [52, 110, 168, 226, 284, 342, 400, 458] },
  quarterFinal: { count: 4, x: 104, y: [81, 197, 313, 429] },
  semiFinal: { count: 2, x: 206, y: [139, 371] },
  final: { count: 1, x: 308, y: [255] },
};
const BRACKET_CARD_WIDTH = 88;
const BRACKET_CARD_HEIGHT = 48;
const BRACKET_WIDTH = 400;
const BRACKET_HEIGHT = 488;
const BRACKET_CONNECTOR_Y_OFFSET = 0;

function isUserMatch(match, entry) {
  return Boolean(entry?.id && (match?.homeId === entry.id || match?.awayId === entry.id));
}

function getPhaseLabel(match) {
  if (match?.phase === 'group') return t('tournamentUx.result.groupStage');
  return BRACKET_PHASE_LABELS[match?.phase] ? t(BRACKET_PHASE_LABELS[match.phase]) : match?.phaseLabel;
}

function getResultLabel(result) {
  const knownResults = ['champion', 'runnerUp', 'semiFinalist', 'quarterFinalist', 'roundOf16', 'groupStage'];
  return knownResults.includes(result?.result) ? t(`tournamentUx.result.${result.result}`) : result?.resultLabel;
}

function hasFinishedRun(tournament, entry) {
  if (!entry || !isKnockoutPhase(tournament)) return false;
  if ((tournament.matches || []).some((match) => KNOCKOUT_PHASES.includes(match.phase) && match.status === 'resolved' && match.loserId === entry.id)) return true;
  return (tournament.standings || []).some((group) => (group.rows || []).some((row) => row.participantId === entry.id && row.rank > 2));
}

function TournamentOverview({ summary }) {
  const tournament = summary.currentTournament;
  const entry = summary.userEntry;
  const application = summary.userApplication;
  const wasNotSelected = application?.status === 'notSelected';
  const wasReassigned = application?.status === 'selected' && application.reassigned;
  const todayMatch = isUserMatch(summary.userTodayMatch, entry)
    ? summary.userTodayMatch
    : (tournament.matches || []).find((match) => isUserMatch(match, entry) && match.dayIndex === tournament.currentDay);
  const canJoin = tournament.status === 'signup';
  const showHeroDetails = tournament.status !== 'scheduled';
  const hasPendingApplication = canJoin && application?.status === 'pending' && !entry;
  const hasParticipation = Boolean(entry || hasPendingApplication);
  const requestedCharacterId = !entry && canJoin ? summary.userApplication?.requestedCharacterId : null;
  const requestedCharacter = requestedCharacterId
    ? PLAYER_IMAGE_OPTIONS.find((character) => character.id === normalizeCharacterId(requestedCharacterId))
    : null;
  const displayedFighter = entry || (requestedCharacterId ? {
    imageId: requestedCharacterId,
    name: requestedCharacter?.label || requestedCharacterId,
  } : null);
  const isOut = hasFinishedRun(tournament, entry);
  const hasTodayMatch = tournament.status === 'active' && Boolean(todayMatch);
  const isTodayResolved = hasTodayMatch && todayMatch.status === 'resolved';
  const isMatchDay = hasTodayMatch && !isTodayResolved;
  const nextMatch = entry && (tournament.matches || [])
    .filter((match) => isUserMatch(match, entry) && match.status !== 'resolved' && match.dayIndex > tournament.currentDay)
    .sort((a, b) => a.dayIndex - b.dayIndex)[0];
  const currentResult = (tournament.finalResults || []).find((result) => result.participantId === entry?.id);
  const participationProgress = tournament.status === 'active' && entry
    ? `${getPhaseLabel(todayMatch || { phase: tournament.currentPhase, phaseLabel: tournament.currentPhaseLabel })} · ${t('learningNavigation.tournament.day', undefined, { day: todayMatch?.dayIndex || tournament.currentDay })}`
    : null;
  let statusKey = 'waitingSignup';
  let hintKey = null;
  let scheduleKey = 'signupOpens';
  let scheduleAt = tournament.signupOpensAt;

  if (tournament.status === 'signup') {
    statusKey = entry ? 'joined' : summary.userApplication ? 'applicationPending' : 'readyToJoin';
    hintKey = 'drawHint';
    scheduleKey = entry ? 'tournamentStarts' : 'signupCloses';
    scheduleAt = entry ? tournament.startAt : tournament.signupClosesAt;
  } else if (tournament.status === 'active') {
    statusKey = !entry ? 'spectator' : isOut ? 'eliminated' : 'competing';
    hintKey = !entry ? 'spectatorHint' : isOut ? 'eliminatedHint' : isMatchDay ? 'practiceHint' : isTodayResolved ? 'matchResolvedHint' : nextMatch ? 'nextMatchHint' : 'waitingHint';
    scheduleKey = isMatchDay ? 'scoreCutoff' : nextMatch && !isOut ? 'nextMatch' : 'tournamentEnds';
    const dayIndex = isMatchDay ? todayMatch.dayIndex : nextMatch?.dayIndex - 1;
    const startTime = new Date(tournament.startAt).getTime();
    scheduleAt = (isMatchDay || (nextMatch && !isOut)) && Number.isFinite(startTime) && Number.isFinite(dayIndex)
      ? new Date(startTime + dayIndex * 86400000).toISOString()
      : (isMatchDay || (nextMatch && !isOut)) ? null : tournament.endAt;
  } else if (tournament.status === 'completed') {
    statusKey = 'completed';
    hintKey = 'completedHint';
    scheduleKey = null;
    scheduleAt = null;
  } else if (tournament.status === 'cancelled') {
    statusKey = 'cancelled';
    scheduleKey = null;
    scheduleAt = null;
  }

  return (
    <section className="tournament-overview" aria-label={t('tournamentUx.overview')}>
      <div className="tournament-overview-hero">
        {showHeroDetails && <div className="tournament-overview-heading">
          <div className="tournament-overview-caption">
            <span className="tournament-eyebrow">{t('tournamentUx.overview')} · {tournament.month}</span>
            <h2>{getStatusCopy(tournament)}</h2>
          </div>
        </div>}
        {showHeroDetails && <div className={`tournament-overview-grid${hasParticipation ? ' is-participant' : ''}`}>
          {hasParticipation ? <>
            {displayedFighter && <div className="tournament-overview-participant-character">
              <ParticipantAvatar participant={displayedFighter} size="md" />
            </div>}
            <div className="tournament-overview-participant-status">
              <strong>
                <span className="tournament-overview-participant-copy" title={hasPendingApplication ? t('tournamentUx.applicationPending') : undefined}>{hasPendingApplication && displayedFighter
                  ? t('tournamentUx.applicationSubmitted', undefined, { name: displayedFighter.name })
                  : t(`tournamentUx.${statusKey}`)}</span>
              </strong>
              {participationProgress && <div><span className="tournament-overview-participant-copy tournament-overview-phase">{participationProgress}</span></div>}
              {!hasPendingApplication && displayedFighter && <div><span className="tournament-overview-participant-copy">{displayedFighter.name}</span></div>}
              {currentResult && <div><span className="tournament-overview-participant-copy">#{currentResult.rank} · {getResultLabel(currentResult)}</span></div>}
            </div>
          </> : <div className="tournament-overview-status">
            <div className="tournament-overview-caption">
               <span className="tournament-detail-label">{t('tournamentUx.entryStatus')}</span>
               <strong>{t(`tournamentUx.${statusKey}`)}</strong>
            </div>
          </div>}
          {scheduleKey && !hasParticipation && (
            <div className="tournament-overview-schedule tournament-overview-caption">
              <span className="tournament-detail-label">{t('tournamentUx.nextSchedule')}</span>
              <strong>{t(`tournamentUx.${scheduleKey}`)}</strong>
              {scheduleAt ? <time dateTime={scheduleAt}>{formatDateTime(scheduleAt)}</time> : <span>{t('tournamentUx.schedulePending')}</span>}
            </div>
          )}
        </div>}
      </div>
      {(hintKey || wasNotSelected || wasReassigned || canJoin || isMatchDay) && (
        <div className="tournament-overview-action">
          {wasNotSelected ? <p>{t('tournamentUx.notSelected')}</p> : hintKey && <p>{t(`tournamentUx.${hintKey}`)}</p>}
          {wasReassigned && <p>
            {t('tournamentUx.reassigned')}
            {application.requestedCharacterId && <> {t('tournamentUx.requestedCharacter', undefined, { name: application.requestedCharacterId })}</>}
          </p>}
          {canJoin && <a className="tournament-primary-btn" href="#tournament-signup">{t('tournamentUx.signupAction')}</a>}
          {isMatchDay && <Link className="tournament-primary-btn" to="/" state={{ startHomePractice: true }} onClick={() => { void unlockPracticeAudio(); }}>{t('tournamentUx.trainAction')}</Link>}
        </div>
      )}
      <div className="tournament-overview-footer">
        <span>{formatDate(tournament.startAt)} – {formatDate(tournament.endAt)} ({t('tournamentUx.duration')}) · {t('tournamentUx.timezone')}</span>
      </div>
    </section>
  );
}

function isKnockoutPhase(tournament) {
  return KNOCKOUT_PHASES.includes(tournament?.currentPhase);
}

function getAliveParticipantIds(tournament) {
  const participants = tournament?.participants || [];
  const matches = tournament?.matches || [];

  if (!isKnockoutPhase(tournament)) {
    return new Set(participants.map((participant) => participant.id));
  }

  const knockoutMatches = matches.filter((match) => KNOCKOUT_PHASES.includes(match.phase));
  if (knockoutMatches.length === 0) {
    const advancedFromGroups = new Set();
    (tournament.standings || []).forEach((standing) => {
      (standing.rows || []).forEach((row) => {
        if (row.rank <= 2) advancedFromGroups.add(row.participantId);
      });
    });
    return advancedFromGroups;
  }

  const latestPhase = KNOCKOUT_PHASES.reduce((latest, phase) => {
    const phaseMatches = knockoutMatches.filter((match) => match.phase === phase);
    return phaseMatches.length > 0 ? phase : latest;
  }, null);

  const latestMatches = knockoutMatches.filter((match) => match.phase === latestPhase);
  const latestResolved = latestMatches.length > 0 && latestMatches.every((match) => match.status === 'resolved');
  const aliveIds = new Set();

  latestMatches.forEach((match) => {
    if (latestResolved && match.winnerId) {
      aliveIds.add(match.winnerId);
      return;
    }

    if (match.homeId) aliveIds.add(match.homeId);
    if (match.awayId) aliveIds.add(match.awayId);
  });

  return aliveIds;
}

function ParticipantAvatar({ participant, mood = 'normal', size = 'md' }) {
  const imageSrc = getParticipantImage(participant, mood);

  if (!imageSrc) {
    return (
      <span
        className={`tournament-avatar tournament-avatar-${size} tournament-avatar-placeholder`}
        aria-label={participant?.name || t('learningNavigation.tournament.participant')}
        role="img"
      />
    );
  }

  return (
    <img
      className={`tournament-avatar tournament-avatar-${size}`}
      src={imageSrc}
      alt={participant?.name || t('learningNavigation.tournament.participant')}
    />
  );
}

function ParticipantRow({ participant, index, isEliminated = false, isUser = false }) {
  return (
    <div className={`tournament-participant-row ${isEliminated ? 'is-eliminated' : ''} ${isUser ? 'is-user' : ''}`.trim()}>
      <span className="tournament-slot">{String(index + 1).padStart(2, '0')}</span>
      <ParticipantAvatar participant={participant} mood={isEliminated && participant.type === 'npc' ? 'depressed' : 'normal'} size="sm" />
      <span className="tournament-participant-name">{participant.name}</span>
      <span className={`tournament-type-badge ${participant.type}`}>
        {t(isEliminated ? 'learningNavigation.tournament.out' : participant.type === 'human' ? 'learningNavigation.tournament.player' : 'learningNavigation.tournament.npc')}
      </span>
    </div>
  );
}

function MatchCard({ match, entry }) {
  if (!match) return null;

  const isResolved = match.status === 'resolved';
  const homeMood = isResolved && match.winnerId === match.homeId ? 'victory'
    : isResolved && match.loserId === match.homeId ? 'flustered' : 'normal';
  const awayMood = isResolved && match.winnerId === match.awayId ? 'victory'
    : isResolved && match.loserId === match.awayId ? 'flustered' : 'normal';

  return (
    <article className={`tournament-match-card ${isResolved ? 'is-resolved' : ''} ${isUserMatch(match, entry) ? 'is-user-match' : ''}`}>
      <div className="tournament-match-meta">
        <span>{getPhaseLabel(match)}</span>
        <span>{t('learningNavigation.tournament.day', undefined, { day: match.dayIndex })}</span>
      </div>
      {isUserMatch(match, entry) && <span className="tournament-user-marker">{t('tournamentUx.yourMatch')}</span>}
      <div className="tournament-match-fighters">
        <div className={`tournament-match-fighter ${match.winnerId === match.homeId ? 'is-winner' : ''} ${match.loserId === match.homeId ? 'is-loser' : ''}`.trim()}>
          <ParticipantAvatar participant={match.home} mood={homeMood} size="sm" />
          {isResolved && match.homeScore != null && <span className="tournament-match-score">{match.homeScore}</span>}
          <strong>{match.home?.name || '-'}</strong>
        </div>
        <em>vs</em>
        <div className={`tournament-match-fighter ${match.winnerId === match.awayId ? 'is-winner' : ''} ${match.loserId === match.awayId ? 'is-loser' : ''}`.trim()}>
          <ParticipantAvatar participant={match.away} mood={awayMood} size="sm" />
          {isResolved && match.awayScore != null && <span className="tournament-match-score">{match.awayScore}</span>}
          <strong>{match.away?.name || '-'}</strong>
        </div>
      </div>
      {!isResolved && <p className="tournament-match-note">{t('tournamentUx.awaitingScore')}</p>}
      {match.tieBroken && <p className="tournament-match-note">{t('learningNavigation.tournament.tie')}</p>}
    </article>
  );
}

export function SignupPanel({ summary, onApplied }) {
  const application = summary.userApplication;
  const options = (summary.characters || PLAYER_IMAGE_OPTIONS).map((c) => ({
    ...c, src: getParticipantImage({ imageId: c.id }), label: c.name || c.label,
  }));
  const initialId = application?.requestedCharacterId || options[0]?.id || '';
  const [characterId, setCharacterId] = useState(initialId);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => setCharacterId(initialId), [initialId]);
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving || !characterId) return;
    setIsSaving(true);
    setError(null);
    try { onApplied(await apiClient.applyToTournament({ characterId })); }
    catch (err) { setError(err.message || t('learningNavigation.tournament.applyError')); }
    finally { setIsSaving(false); }
  };
  return (
    <section className="tournament-section tournament-signup" id="tournament-signup" tabIndex={-1}>
      <div className="tournament-section-heading">
        <h2>{t('tournamentUx.chooseCharacter')}</h2>
        <span>{t('tournamentUx.applicantCount', undefined, { count: summary.applicationCount || 0 })}</span>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="tournament-player-grid">
          {options.map((option) => (
            <button key={option.id} type="button" className={option.id === characterId ? 'is-selected' : ''}
              onClick={() => setCharacterId(option.id)} aria-pressed={option.id === characterId}
              disabled={isSaving} title={option.label}>
              <img src={option.src} alt="" />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
        {error && <p className="tournament-form-error" role="alert">{error}</p>}
        <button className="tournament-primary-btn" disabled={isSaving || !characterId}>
          {t(isSaving ? 'learningNavigation.tournament.saving' : application ? 'tournamentUx.changeCharacter' : 'learningNavigation.tournament.join')}
        </button>
      </form>
    </section>
  );
}

function ParticipantsPanel({ tournament, entry }) {
  if (!tournament?.participants?.length) return null;
  const aliveIds = getAliveParticipantIds(tournament);
  const canShowEliminated = isKnockoutPhase(tournament) && aliveIds.size > 0;

  return (
    <section className="tournament-section">
      <div className="tournament-section-heading">
        <h2>{t('learningNavigation.tournament.fightersTitle')}</h2>
        <span>{t('learningNavigation.tournament.participants', undefined, { players: getHumanCount(tournament), npcs: 32 - getHumanCount(tournament) })}</span>
      </div>
      <div className="tournament-participant-grid">
        {tournament.participants.map((participant, index) => (
          <ParticipantRow
            key={`${participant.id}-${participant.slot}`}
            participant={participant}
            index={index}
            isEliminated={canShowEliminated && !aliveIds.has(participant.id)}
            isUser={participant.id === entry?.id}
          />
        ))}
      </div>
    </section>
  );
}

function UserGroupPanel({ tournament, entry, todayMatch }) {
  const participantMap = useMemo(() => new Map((tournament?.participants || []).map((p) => [p.id, p])), [tournament]);
  if (!entry || !tournament?.groups?.length) return null;

  const group = tournament.groups.find((candidate) => candidate.entrantIds.includes(entry.id));
  const standing = tournament.standings?.find((candidate) => candidate.groupId === group?.id);

  return (
    <section className="tournament-section tournament-user-group">
      <div className="tournament-section-heading">
        <h2>{t('learningNavigation.tournament.yourGroup', undefined, { group: group?.id || '' })}</h2>
        <span>{t(todayMatch ? 'learningNavigation.tournament.todayReady' : 'learningNavigation.tournament.noMatch')}</span>
      </div>

      {todayMatch && <MatchCard match={todayMatch} entry={entry} />}

      <div className="tournament-standing-table">
        <div className="tournament-standing-head">
          <span>{t('learningNavigation.tournament.rank')}</span>
          <span>{t('learningNavigation.tournament.fighter')}</span>
          <span>{t('learningNavigation.tournament.wins')}</span>
          <span>{t('learningNavigation.common.score')}</span>
        </div>
        {(standing?.rows || []).map((row) => {
          const participant = participantMap.get(row.participantId);
          return (
            <div key={row.participantId} className={row.participantId === entry.id ? 'is-user' : ''}>
              <span>{row.rank}</span>
              <span>
                <ParticipantAvatar participant={participant} size="xs" />
                {row.name}
              </span>
              <span>{row.wins}</span>
              <span>{row.totalScore} pt</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MatchesPanel({ tournament, entry }) {
  const participantMap = useMemo(() => new Map((tournament?.participants || []).map((p) => [p.id, p])), [tournament]);
  if (!tournament?.matches?.length) return null;

  const visibleMatches = tournament.matches
    .filter((match) => match.dayIndex === tournament.currentDay || match.status === 'resolved')
    .slice(-12)
    .map((match) => ({
      ...match,
      home: participantMap.get(match.homeId),
      away: participantMap.get(match.awayId),
    }));

  return (
    <section className="tournament-section">
      <div className="tournament-section-heading">
        <h2>{t('learningNavigation.tournament.matches')}</h2>
        <span>{t('learningNavigation.tournament.latest')}</span>
      </div>
      <div className="tournament-match-grid">
        {visibleMatches.map((match) => <MatchCard key={match.id} match={match} entry={entry} />)}
      </div>
    </section>
  );
}

function getMatchIndex(match) {
  const rawIndex = Number(match?.id?.split('_').pop());
  return Number.isFinite(rawIndex) ? rawIndex - 1 : 0;
}

function buildPhaseSlots(matches, phase, count) {
  const slots = Array.from({ length: count }, (_, index) => ({
    id: `placeholder-${phase}-${index}`,
    phase,
    dayIndex: phase === 'roundOf16' ? 4 : phase === 'quarterFinal' ? 5 : phase === 'semiFinal' ? 6 : 7,
    status: 'pending',
    homeId: null,
    awayId: null,
    homeScore: null,
    awayScore: null,
    winnerId: null,
    loserId: null,
    isPlaceholder: true,
    bracketIndex: index,
  }));

  matches
    .filter((match) => match.phase === phase)
    .forEach((match) => {
      const index = Math.max(0, Math.min(count - 1, getMatchIndex(match)));
      slots[index] = { ...match, bracketIndex: index };
    });

  return slots;
}

function buildBracketModel(tournament) {
  const matches = tournament?.matches || [];
  const model = {};

  for (const phase of KNOCKOUT_PHASES) {
    model[phase] = buildPhaseSlots(matches, phase, BRACKET_LAYOUT[phase].count);
  }

  return model;
}

function BracketConnectors({ bracketModel, entry }) {
  const lines = [];

  const addLine = (fromPhase, fromIndex, toPhase, toIndex) => {
    const fromLayout = BRACKET_LAYOUT[fromPhase];
    const toLayout = BRACKET_LAYOUT[toPhase];
    const x1 = fromLayout.x + BRACKET_CARD_WIDTH;
    const y1 = fromLayout.y[fromIndex] + BRACKET_CONNECTOR_Y_OFFSET;
    const x2 = toLayout.x;
    const y2 = toLayout.y[toIndex] + BRACKET_CONNECTOR_Y_OFFSET;
    const midX = (x1 + x2) / 2;
    const fromMatch = bracketModel[fromPhase]?.[fromIndex];
    const toMatch = bracketModel[toPhase]?.[toIndex];
    const isResolved = fromMatch?.status === 'resolved';
    const isActive = toMatch?.status && toMatch.status !== 'pending' && toMatch.status !== 'resolved';
    const isUserPath = Boolean(entry?.id && fromMatch?.winnerId === entry.id && isUserMatch(toMatch, entry));

    lines.push({
      id: `${fromPhase}-${fromIndex}-${toPhase}-${toIndex}`,
      d: `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`,
      className: `tournament-bracket-connector ${isResolved ? 'is-resolved' : ''} ${isActive ? 'is-active' : ''} ${isUserPath ? 'is-user-path' : ''}`.trim(),
    });
  };

  for (let index = 0; index < 8; index += 1) addLine('roundOf16', index, 'quarterFinal', Math.floor(index / 2));
  for (let index = 0; index < 4; index += 1) addLine('quarterFinal', index, 'semiFinal', Math.floor(index / 2));
  for (let index = 0; index < 2; index += 1) addLine('semiFinal', index, 'final', 0);

  return (
    <svg className="tournament-bracket-connectors" viewBox={`0 0 ${BRACKET_WIDTH} ${BRACKET_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
      {lines.map((line) => (
        <path key={line.id} d={line.d} className={line.className} />
      ))}
    </svg>
  );
}

function TournamentFinalFocus({ match, participantMap, entry }) {
  if (!match || match.isPlaceholder || (!match.homeId && !match.awayId)) return null;

  const home = participantMap.get(match.homeId);
  const away = participantMap.get(match.awayId);
  const isResolved = match.status === 'resolved';

  const renderFinalist = (participant, sideId, score) => {
    const isWinner = isResolved && match.winnerId === sideId;
    const isLoser = isResolved && match.loserId === sideId;
    const isUser = entry?.id === sideId;

    return (
      <div className={`tournament-final-fighter ${isWinner ? 'is-winner' : ''} ${isLoser ? 'is-loser' : ''} ${isUser ? 'is-user' : ''}`.trim()}>
        <ParticipantAvatar
          participant={participant}
          mood={isLoser && participant?.type === 'npc' ? 'depressed' : 'normal'}
          size="md"
        />
        <strong>{participant?.name || t('learningNavigation.tournament.tbd')}</strong>
        <span>{isResolved ? `${score} pt` : '-'}</span>
      </div>
    );
  };

  return (
    <div className="tournament-final-focus" aria-label={t('learningNavigation.tournament.finalMatch')}>
      <div className="tournament-final-focus-heading">
        <span>{t('learningNavigation.tournament.finalMatch')}</span>
        <strong>{t('learningNavigation.tournament.day', undefined, { day: match.dayIndex || 7 })}</strong>
      </div>
      <div className="tournament-final-fighters">
        {renderFinalist(home, match.homeId, match.homeScore)}
        <em>VS</em>
        {renderFinalist(away, match.awayId, match.awayScore)}
      </div>
    </div>
  );
}

function BracketMatchCard({
  match,
  participantMap,
  entry,
  phase,
  isCurrentPhase = false,
}) {
  if (!match) return null;

  const home = participantMap.get(match.homeId);
  const away = participantMap.get(match.awayId);
  const isResolved = match.status === 'resolved';
  const isPending = match.isPlaceholder || (!match.homeId && !match.awayId);
  const imageOnly = phase !== 'roundOf16';

  const renderFighter = (participant, sideId) => {
    const isWinner = isResolved && match.winnerId === sideId;
    const isLoser = isResolved && match.loserId === sideId;
    const isUser = entry?.id === sideId;
    return (
      <div
        className={`tournament-bracket-fighter ${isWinner ? 'is-winner' : ''} ${isLoser ? 'is-loser' : ''} ${isUser ? 'is-user' : ''} ${!participant ? 'is-empty' : ''}`.trim()}
        role={imageOnly ? 'group' : undefined}
        aria-label={imageOnly ? `${participant?.name || t('learningNavigation.tournament.tbd')}${isUser ? ` (${t('tournamentUx.you')})` : ''}` : undefined}
        title={imageOnly ? participant?.name : undefined}
      >
        {participant ? (
          <ParticipantAvatar
            participant={participant}
            mood={isLoser && participant?.type === 'npc' ? 'depressed' : 'normal'}
            size="xs"
          />
        ) : (
          <span className="tournament-bracket-empty-avatar" />
        )}
        {!imageOnly && <span className="tournament-bracket-fighter-name" title={participant?.name}>
          {participant?.name || t('learningNavigation.tournament.tbd')}
          {isUser && <small className="tournament-user-marker">{t('tournamentUx.you')}</small>}
        </span>}
      </div>
    );
  };
  return (
    <div
      className={`tournament-bracket-match ${imageOnly ? 'is-image-only' : ''} ${isResolved ? 'is-resolved' : ''} ${isPending ? 'is-pending' : ''} ${isCurrentPhase ? 'is-current-phase' : ''} ${isUserMatch(match, entry) ? 'is-user-match' : ''}`.trim()}
      style={{
        left: `${BRACKET_LAYOUT[phase].x / BRACKET_WIDTH * 100}%`,
        width: `${BRACKET_CARD_WIDTH / BRACKET_WIDTH * 100}%`,
        top: `${BRACKET_LAYOUT[phase].y[match.bracketIndex || 0] - (BRACKET_CARD_HEIGHT * (imageOnly ? 2 : 1) / 2)}px`,
      }}
    >
      {renderFighter(home, match.homeId, match.homeScore)}
      {renderFighter(away, match.awayId, match.awayScore)}
    </div>
  );
}

function TournamentBracketPanel({ tournament, entry }) {
  const [view, setView] = useState('bracket');
  const participantMap = useMemo(() => new Map((tournament?.participants || []).map((p) => [p.id, p])), [tournament]);
  const bracketModel = useMemo(() => buildBracketModel(tournament), [tournament]);
  const finalMatch = bracketModel.final?.[0] || null;
  if (!isKnockoutPhase(tournament) && !(tournament?.matches || []).some((match) => KNOCKOUT_PHASES.includes(match.phase))) return null;

  return (
    <section className="tournament-section tournament-bracket-section">
      <div className="tournament-section-heading">
        <h2>{t('learningNavigation.tournament.bracket')}</h2>
        <div className="tournament-view-controls" role="group" aria-label={t('tournamentUx.viewLabel')}>
          <button type="button" aria-pressed={view === 'bracket'} onClick={() => setView('bracket')}>{t('tournamentUx.bracketView')}</button>
          <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>{t('tournamentUx.listView')}</button>
        </div>
      </div>
      {entry && <p className="tournament-path-hint"><span className="tournament-user-marker">{t('tournamentUx.you')}</span> {t('tournamentUx.pathHint')}</p>}
      {view === 'bracket' && (
        <>
      <div className="tournament-bracket-scroll" tabIndex={0} role="region" aria-label={t('learningNavigation.tournament.bracketDescription')}>
        <div className="tournament-bracket" aria-label={t('learningNavigation.tournament.bracketDescription')}>
          <BracketConnectors bracketModel={bracketModel} entry={entry} />
          {BRACKET_PHASES.map((phase) => (
            <div
              key={`${phase}-label`}
              className={`tournament-bracket-phase-label ${tournament.currentPhase === phase ? 'is-current-phase' : ''}`}
              style={{ left: `${BRACKET_LAYOUT[phase].x / BRACKET_WIDTH * 100}%` }}
            >
              {t(BRACKET_PHASE_LABELS[phase])}
            </div>
          ))}
          {BRACKET_PHASES.flatMap((phase) => (
            bracketModel[phase].map((match) => (
              <BracketMatchCard
                key={match.id}
                match={match}
                participantMap={participantMap}
                entry={entry}
                phase={phase}
                isCurrentPhase={tournament.currentPhase === phase}
              />
            ))
          ))}
        </div>
      </div>
        </>
      )}
      {view === 'list' && (
        <div className="tournament-bracket-list" role="region" aria-label={t('tournamentUx.listView')}>
          <TournamentFinalFocus match={finalMatch} participantMap={participantMap} entry={entry} />
          {KNOCKOUT_PHASES.map((phase) => {
            const matches = bracketModel[phase].filter((match) => !match.isPlaceholder);
            return (
              <section key={phase} className="tournament-round-list" aria-label={t(BRACKET_PHASE_LABELS[phase])}>
                <h3>{t(BRACKET_PHASE_LABELS[phase])}</h3>
                {matches.length ? (
                  <div className="tournament-match-grid">
                    {matches.map((match) => <MatchCard key={match.id} match={{ ...match, home: participantMap.get(match.homeId), away: participantMap.get(match.awayId) }} entry={entry} />)}
                  </div>
                ) : <p className="tournament-muted">{t('tournamentUx.roundPending')}</p>}
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ResultsPanel({ tournament, title = t('learningNavigation.tournament.results'), userId = null, collapsible = false }) {
  const [expanded, setExpanded] = useState(false);
  const toggleRef = useRef(null);
  const listId = useId();
  const results = tournament?.finalResults || [];
  const ownResult = userId ? results.find((result) => result.userId === userId) : null;
  const otherResults = results.filter((result) => result !== ownResult);
  if (!results.length) return null;

  const renderResult = (result) => (
    <div key={result.participantId}
      className={`tournament-result-row ${userId && result.userId === userId ? 'is-user' : ''}`.trim()}>
      <span className="tournament-rank">#{result.rank}</span>
      <ParticipantAvatar participant={result} size="sm" />
      <strong>{result.name}</strong>
      <span>{result.totalScore} pt</span>
      <em>{getResultLabel(result)}</em>
    </div>
  );

  return (
    <section className={`tournament-section${collapsible ? ' tournament-past-results' : ''}`}>
      <div className="tournament-section-heading">
        <h2>{title}</h2>
        <span>{tournament.month}</span>
      </div>
      {collapsible ? (
        <>
          {ownResult ? (
            <div className="tournament-results" role="group" aria-label={t('tournamentUx.myResult')}>
              <h3 className="tournament-my-result-title">{t('tournamentUx.myResult')}</h3>
              {renderResult(ownResult)}
            </div>
          ) : <p className="tournament-muted">{t('tournamentUx.noOwnResult')}</p>}
          {otherResults.length > 0 && (
            <>
              <button ref={toggleRef} type="button" className="tournament-results-toggle"
                aria-expanded={expanded} aria-controls={listId} onClick={() => setExpanded((value) => !value)}>
                <span>{t(expanded ? 'tournamentUx.collapseResults' : ownResult ? 'tournamentUx.otherResults' : 'tournamentUx.allResults', undefined, { count: otherResults.length })}</span>
                {expanded ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
              </button>
              <div id={listId} hidden={!expanded}>
                {expanded && <div className="tournament-results">{otherResults.map(renderResult)}</div>}
                {expanded && <button type="button" className="tournament-results-toggle tournament-results-collapse"
                  aria-expanded={expanded} aria-controls={listId} onClick={() => {
                    setExpanded(false);
                    toggleRef.current?.focus();
                  }}>
                  <span>{t('tournamentUx.collapseResults')}</span>
                  <ChevronUp size={18} aria-hidden="true" />
                </button>}
              </div>
            </>
          )}
        </>
      ) : <div className="tournament-results">{results.map(renderResult)}</div>}
    </section>
  );
}

function TournamentPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSummary = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getTournamentSummary();
      setSummary(data);
    } catch (err) {
      setError(err.message || t('learningNavigation.tournament.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const tournament = summary?.currentTournament;
  const lastTournament = summary?.lastTournament;
  const currentUserId = authService.getCurrentUserId();
  const showKnockoutBracket = isKnockoutPhase(tournament);
  const shouldShowLastTournament =
    lastTournament?.finalResults?.length > 0 &&
    ['scheduled', 'cancelled'].includes(tournament?.status);

  return (
    <div className="tournament-page">
      <div className="tournament-page-header">
        <button onClick={() => navigate('/')} className="btn-back-inline" title={t('learningNavigation.common.backHome')} aria-label={t('learningNavigation.common.backHome')}>
          <ArrowLeft size={24} strokeWidth={2.4} aria-hidden="true" />
        </button>
        <div>
          <h1 className="page-title-fx page-title-tournament">{t('learningNavigation.tournament.title')}</h1>
          <p>{t('learningNavigation.tournament.subtitle', undefined, { timezone: summary?.timezone || 'Asia/Seoul' })}</p>
        </div>
      </div>

      {isLoading && <div className="tournament-loading" role="status">{t('learningNavigation.tournament.loading')}</div>}

      {error && (
        <div className="tournament-error" role="alert">
          <p>{error}</p>
          <button onClick={loadSummary}>{t('learningNavigation.common.retry')}</button>
        </div>
      )}

      {!isLoading && !error && tournament && (
        <>
          <TournamentOverview summary={summary} />

          {tournament.status === 'scheduled' && (
            <section className="tournament-section">
              <div className="tournament-section-heading">
                <h2>{t('learningNavigation.tournament.nextSchedule')}</h2>
                <span>{t('learningNavigation.tournament.signupOpens', undefined, { date: formatDateTime(tournament.signupOpensAt) })}</span>
              </div>
              <p className="tournament-muted">
                {t('learningNavigation.tournament.scheduleHint')}
              </p>
            </section>
          )}

          {tournament.status === 'signup' && (
            <>
              <SignupPanel summary={summary} onApplied={setSummary} />
              {summary.userEntry && (
                <ParticipantsPanel tournament={tournament} entry={summary.userEntry} />
              )}
            </>
          )}

          {tournament.status === 'active' && (
            <>
              {showKnockoutBracket ? (
                <TournamentBracketPanel tournament={tournament} entry={summary.userEntry} />
              ) : (
                <>
                  <UserGroupPanel
                    tournament={tournament}
                    entry={summary.userEntry}
                    todayMatch={summary.userTodayMatch}
                  />
                  <MatchesPanel tournament={tournament} entry={summary.userEntry} />
                </>
              )}
            </>
          )}

          {tournament.status === 'cancelled' && (
            <section className="tournament-section">
              <div className="tournament-section-heading">
                <h2>{t('learningNavigation.tournament.cancelled')}</h2>
                <span>{t('learningNavigation.tournament.noApplicants')}</span>
              </div>
              <p className="tournament-muted">{t('learningNavigation.tournament.cancelledHint')}</p>
            </section>
          )}

          {tournament.status === 'completed' && (
            <>
              <ResultsPanel tournament={tournament} userId={currentUserId} />
              <TournamentBracketPanel tournament={tournament} entry={summary.userEntry} />
            </>
          )}

          {shouldShowLastTournament && (
            <ResultsPanel key={lastTournament.id || lastTournament.month} tournament={lastTournament} title={t('learningNavigation.tournament.last')} userId={currentUserId} collapsible />
          )}
        </>
      )}
      {!isLoading && !error && !tournament && (
        <section className="tournament-section">
          <h2>{t('tournamentUx.noTournament')}</h2>
          <p className="tournament-muted">{t('tournamentUx.retryHint')}</p>
          <button className="tournament-primary-btn" onClick={loadSummary}>{t('learningNavigation.common.retry')}</button>
        </section>
      )}
    </div>
  );
}

export default TournamentPage;
